import { z } from 'zod/v4';

const DecimalStringRuntype = z.string();
const CountRuntype = z.number().int().nonnegative().max(4_294_967_295);

export type AppChannel = z.infer<typeof AppChannelRuntype>;
export const AppChannelRuntype = z.enum(['release', 'draft']);

export type ModelClass = z.infer<typeof ModelClassRuntype>;
export const ModelClassRuntype = z.enum(['fast', 'balanced', 'capable', 'reasoning', 'decision']);

const RESERVED_TENANT_NAMES = ['api', 'suppliers'];

export const AppOwnerRuntype = z.union([
    z.literal('*'),
    z
        .string()
        .regex(/^[A-Za-z0-9_-]+$/)
        .refine((tenant) => !RESERVED_TENANT_NAMES.includes(tenant)),
]);

export interface AppCall extends z.infer<typeof AppCallRuntype> {}
export const AppCallRuntype = z.object({
    owner: AppOwnerRuntype,
    appId: z.string().min(1),
    channel: AppChannelRuntype,
    modelClass: ModelClassRuntype,
    verified: z.boolean(),
});

export type LlmCaller = z.infer<typeof LlmCallerRuntype>;
export const LlmCallerRuntype = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('product'), component: z.string() }),
    z.object({ kind: z.literal('app_completion'), app: AppCallRuntype }),
    z.object({ kind: z.literal('app_decision'), app: AppCallRuntype }),
    z.object({ kind: z.literal('app_assistant'), app: AppCallRuntype, assistantId: z.string() }),
]);

export interface TokenUsage extends z.infer<typeof TokenUsageRuntype> {}
export const TokenUsageRuntype = z.object({
    input: CountRuntype,
    output: CountRuntype,
    total: CountRuntype,
    cachedInput: CountRuntype.nullable().optional(),
    reasoning: CountRuntype.nullable().optional(),
});

const ReportedUsageShape = {
    tokens: TokenUsageRuntype,
    finishReason: z.string().optional(),
    costUsd: DecimalStringRuntype,
};

export type LlmUsageOutcome = z.infer<typeof LlmUsageOutcomeRuntype>;
export const LlmUsageOutcomeRuntype = z.discriminatedUnion('status', [
    z.object({ status: z.literal('ok'), ...ReportedUsageShape }),
    z.object({ status: z.literal('incomplete'), ...ReportedUsageShape }),
    z.object({ status: z.literal('usage_missing'), estimatedCostUsd: DecimalStringRuntype }),
]);

export interface LlmUsageRow extends z.infer<typeof LlmUsageRowRuntype> {}
export const LlmUsageRowRuntype = z.object({
    requestId: z.uuid(),
    stepIndex: CountRuntype,
    providerResponseId: z.string().nullable().optional(),
    apimRequestId: z.string().nullable().optional(),
    tenant: z.string(),
    subject: z.string().nullable().optional(),
    caller: LlmCallerRuntype,
    deployment: z.string(),
    responseModel: z.string().nullable().optional(),
    streamed: z.boolean(),
    outcome: LlmUsageOutcomeRuntype,
    priceVersion: z.string(),
    ratedAt: z.string(),
    occurredAt: z.string(),
    finishedAt: z.string().nullable().optional(),
    latencyMs: CountRuntype.nullable().optional(),
});

export interface RecordLlmUsageRequest extends z.infer<typeof RecordLlmUsageRequestRuntype> {}
export const RecordLlmUsageRequestRuntype = z.object({
    rows: z.array(LlmUsageRowRuntype),
});

export interface CheckLlmCapsRequest extends z.infer<typeof CheckLlmCapsRequestRuntype> {}
export const CheckLlmCapsRequestRuntype = z.object({
    tenant: z.string(),
    app: z
        .object({
            owner: z.string(),
            appId: z.string(),
        })
        .nullable()
        .optional(),
});

export type CheckLlmCapsResponse = z.infer<typeof CheckLlmCapsResponseRuntype>;
export const CheckLlmCapsResponseRuntype = z.discriminatedUnion('allowed', [
    z.object({ allowed: z.literal(true) }),
    z.object({
        allowed: z.literal(false),
        scope: z.enum(['tenant', 'app']),
        reason: z.enum(['exhausted', 'zero']),
    }),
]);
